import { Checkbox, Dropdown, Tag } from '@carbon/react';
import {
  asProblem,
  ListTable,
  PageHeader,
  problemMessage,
  StatTile,
  StatTiles,
  useFormat,
  useReferenceOptions,
  type ListColumn,
} from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { hoApi } from '../api';
import { ReportFrame } from '../reports/reportFrame';
import { unwrap, useReportRange, type ReportRange } from '../reports/reportRange';
import '../reports/Reports.scss';

type S = HoComponents['schemas'];
type Balance = S['HoStockBalanceDto'];
type Movement = S['HoStockMovementDto'];
type MovementTotal = S['HoStockMovementTotalDto'];
type Transfer = S['HoStockTransferDto'];
type Count = S['HoStockCountDto'];
type TransferLine = Transfer['lines'][number];
type CountLine = Count['lines'][number];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const signed = (format: (v: number) => string, value: number) => (value > 0 ? '+' : '') + format(value);

function useStockText() {
  const { t } = useTranslation();
  return (key: string, options?: Record<string, unknown>) => t(`reports.stock.${key}` as I18nKey, options);
}

function usePaging() {
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  return {
    ...paging,
    reset: () => setPaging((p) => ({ ...p, page: 1 })),
    pagination: (total: number | string | undefined) => ({ ...paging, total: n(total), onChange: setPaging }),
  };
}

/** All stores or one, as on the reports; plus the page's own filters. */
function StockFilters({
  storeId,
  onStore,
  children,
}: {
  storeId: string;
  onStore(id: string): void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const stores = useReferenceOptions('org.store');
  const items = [
    { id: '', label: t('reports.allStores') },
    ...(stores.data ?? []).map((s) => ({ id: s.value, label: s.label })),
  ];
  return (
    <div className="pos-report-filters">
      <div className="pos-report-filters__field">
        <Dropdown
          id="stock-store"
          titleText={t('reports.store')}
          label={t('reports.store')}
          items={items}
          itemToString={(o) => o?.label ?? ''}
          selectedItem={items.find((o) => o.id === storeId) ?? items[0]}
          onChange={({ selectedItem }) => onStore(selectedItem?.id ?? '')}
        />
      </div>
      {children}
    </div>
  );
}

/** What every store holds of each stock-tracked item, valued at standard cost; stock below zero can be shown alone. */
export function HoStockBalancesPage() {
  const { t } = useTranslation();
  const r = useStockText();
  const format = useFormat();
  const [storeId, setStoreId] = useState('');
  const [search, setSearch] = useState('');
  const [belowZero, setBelowZero] = useState(false);
  const paging = usePaging();
  const list = useQuery({
    queryKey: ['stock', 'ho', 'balances', storeId, search, belowZero, paging.page, paging.pageSize],
    queryFn: () =>
      unwrap(
        hoApi.GET('/api/v1/stock/ho/balances', {
          params: {
            query: {
              storeId: storeId || undefined,
              search: search || undefined,
              belowZero: belowZero || undefined,
              page: paging.page,
              pageSize: paging.pageSize,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const columns: ListColumn<Balance & { id: string }>[] = [
    { key: 'storeName', header: r('store') },
    { key: 'item', header: r('item'), render: (b) => `${b.itemCode} – ${b.itemName}` },
    {
      key: 'onHand',
      header: r('onHand'),
      numeric: true,
      render: (b) => (n(b.onHand) < 0 ? <Tag type="red">{format.number(b.onHand)}</Tag> : format.number(b.onHand)),
    },
    {
      key: 'unitCost',
      header: r('unitCost'),
      numeric: true,
      render: (b) => (b.unitCost == null ? '' : format.number(b.unitCost)),
    },
    { key: 'value', header: r('value'), numeric: true, render: (b) => (b.value == null ? '' : format.number(b.value)) },
    { key: 'lastTxnAt', header: r('lastMovement'), render: (b) => format.dateTime(b.lastTxnAt) },
  ];

  return (
    <section className="pos-admin-page pos-report">
      <PageHeader title={r('balances')} description={r('balancesHint')} />
      <StockFilters
        storeId={storeId}
        onStore={(id) => {
          setStoreId(id);
          paging.reset();
        }}
      >
        <Checkbox
          id="stock-below-zero"
          labelText={r('belowZeroOnly')}
          checked={belowZero}
          onChange={(_e, { checked }) => {
            setBelowZero(checked);
            paging.reset();
          }}
        />
      </StockFilters>
      <StatTiles>
        <StatTile
          label={r('valueTotal')}
          value={format.number(n(list.data?.valueTotal))}
          caption={r('valueCaption')}
          isLoading={list.isLoading}
        />
        <StatTile label={r('lines')} value={format.number(n(list.data?.total))} isLoading={list.isLoading} />
      </StatTiles>
      <ListTable
        columns={columns}
        rows={(list.data?.items ?? []).map((b) => ({ ...b, id: `${b.storeId}:${b.itemId}` }))}
        ariaLabel={r('balances')}
        loading={list.isLoading}
        error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
        onRetry={() => void list.refetch()}
        search={{
          label: r('searchItems'),
          onChange: (value) => {
            setSearch(value);
            paging.reset();
          },
        }}
        pagination={paging.pagination(list.data?.total)}
        empty={{ heading: r('noBalances') }}
      />
    </section>
  );
}

/** The local days of a report range as the instants the API takes. */
function instants(range: ReportRange) {
  const end = new Date(`${range.to}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return { from: new Date(`${range.from}T00:00:00`).toISOString(), to: end.toISOString() };
}

const TXN_TYPES = ['sale', 'receipt', 'waste', 'adjustment', 'count', 'transfer_out', 'transfer_in'] as const;

/** Stock movements across stores in a period: totals by type with their value, then every movement, newest first. */
export function HoStockMovementsPage() {
  const r = useStockText();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const [type, setType] = useState('');
  const paging = usePaging();
  const report = useQuery({
    queryKey: ['stock', 'ho', 'movements', range, type, paging.page, paging.pageSize],
    queryFn: () =>
      unwrap(
        hoApi.GET('/api/v1/stock/ho/movements', {
          params: {
            query: {
              ...instants(range),
              storeId: range.storeId || undefined,
              type: type || undefined,
              page: paging.page,
              pageSize: paging.pageSize,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const data = report.data;
  const typeItems = [{ id: '', label: r('allTypes') }, ...TXN_TYPES.map((v) => ({ id: v, label: r(`txn.${v}`) }))];
  const csv = () => [
    [r('when'), r('store'), r('item'), r('type'), r('quantity'), r('unitCost'), r('note')],
    ...(data?.items ?? []).map((m) => [
      m.occurredAt,
      m.storeName,
      `${m.itemCode} – ${m.itemName}`,
      r(`txn.${m.txnType}`),
      n(m.quantity),
      m.unitCost == null ? '' : n(m.unitCost),
      m.note ?? '',
    ]),
  ];

  return (
    <ReportFrame
      title={r('movements')}
      description={r('movementsHint')}
      range={range}
      onRange={(next) => {
        setRange(next);
        paging.reset();
      }}
      error={report.error}
      csvName="stock-movements"
      csv={(data?.items.length ?? 0) > 0 ? csv : undefined}
      filters={
        <div className="pos-report-filters__field">
          <Dropdown
            id="stock-movement-type"
            titleText={r('type')}
            label={r('type')}
            items={typeItems}
            itemToString={(o) => o?.label ?? ''}
            selectedItem={typeItems.find((o) => o.id === type) ?? typeItems[0]}
            onChange={({ selectedItem }) => {
              setType(selectedItem?.id ?? '');
              paging.reset();
            }}
          />
        </div>
      }
    >
      <ListTable<MovementTotal & { id: string }>
        title={r('byType')}
        description={r('byTypeHint')}
        columns={[
          { key: 'txnType', header: r('type'), render: (x) => r(`txn.${x.txnType}`) },
          { key: 'count', header: r('count'), numeric: true, render: (x) => format.number(n(x.count)) },
          {
            key: 'quantity',
            header: r('quantity'),
            numeric: true,
            render: (x) => signed(format.number, n(x.quantity)),
          },
          {
            key: 'value',
            header: r('value'),
            numeric: true,
            render: (x) => (x.value == null ? '' : signed(format.number, n(x.value))),
          },
        ]}
        rows={(data?.totals ?? []).map((x) => ({ ...x, id: x.txnType }))}
        ariaLabel={r('byType')}
        loading={report.isLoading}
        empty={{ heading: r('noMovements') }}
      />
      <ListTable<Movement>
        title={r('movementList')}
        columns={[
          { key: 'occurredAt', header: r('when'), render: (m) => format.dateTime(m.occurredAt) },
          { key: 'storeName', header: r('store') },
          { key: 'item', header: r('item'), render: (m) => `${m.itemCode} – ${m.itemName}` },
          { key: 'txnType', header: r('type'), render: (m) => <Tag type="gray">{r(`txn.${m.txnType}`)}</Tag> },
          {
            key: 'quantity',
            header: r('quantity'),
            numeric: true,
            render: (m) => signed(format.number, n(m.quantity)),
          },
          { key: 'note', header: r('note'), render: (m) => m.note ?? '' },
        ]}
        rows={data?.items ?? []}
        ariaLabel={r('movementList')}
        loading={report.isLoading}
        pagination={paging.pagination(data?.total)}
        empty={{ heading: r('noMovements') }}
      />
    </ReportFrame>
  );
}

/** Transfers between stores: what was sent, what arrived and the differences, line by line for the one selected. */
export function HoStockTransfersPage() {
  const { t } = useTranslation();
  const r = useStockText();
  const format = useFormat();
  const [storeId, setStoreId] = useState('');
  const [differences, setDifferences] = useState(false);
  const [selected, setSelected] = useState<Transfer>();
  const paging = usePaging();
  const list = useQuery({
    queryKey: ['stock', 'ho', 'transfers', storeId, differences, paging.page, paging.pageSize],
    queryFn: () =>
      unwrap(
        hoApi.GET('/api/v1/stock/ho/transfers', {
          params: {
            query: {
              storeId: storeId || undefined,
              differences: differences || undefined,
              page: paging.page,
              pageSize: paging.pageSize,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const state = (x: Transfer) => (x.received ? 'received' : x.status);
  const columns: ListColumn<Transfer>[] = [
    { key: 'transferNumber', header: r('number') },
    { key: 'fromStoreName', header: r('fromStore') },
    { key: 'toStoreName', header: r('toStore') },
    {
      key: 'status',
      header: r('status'),
      render: (x) => <Tag type={x.received ? 'green' : 'purple'}>{r(`state.${state(x)}`)}</Tag>,
    },
    { key: 'sentAt', header: r('sentAt'), render: (x) => format.dateTime(x.sentAt) },
    { key: 'receivedAt', header: r('receivedAt'), render: (x) => format.dateTime(x.receivedAt) },
    { key: 'lineCount', header: r('lineCount'), numeric: true, render: (x) => format.number(n(x.lineCount)) },
    {
      key: 'differenceLines',
      header: r('differenceLines'),
      numeric: true,
      render: (x) =>
        n(x.differenceLines) > 0 ? <Tag type="red">{format.number(n(x.differenceLines))}</Tag> : format.number(0),
    },
  ];
  const lineColumns: ListColumn<TransferLine>[] = [
    { key: 'item', header: r('item'), render: (l) => `${l.itemCode} – ${l.itemName}` },
    {
      key: 'quantitySent',
      header: r('quantitySent'),
      numeric: true,
      render: (l) => `${format.number(l.quantitySent)} ${l.uomCode}`,
    },
    {
      key: 'quantityReceived',
      header: r('quantityReceived'),
      numeric: true,
      render: (l) => (l.quantityReceived == null ? '' : format.number(l.quantityReceived)),
    },
    {
      key: 'difference',
      header: r('missing'),
      numeric: true,
      render: (l) => (l.difference == null ? '' : format.number(l.difference)),
    },
  ];

  return (
    <section className="pos-admin-page pos-report">
      <PageHeader title={r('transfers')} description={r('transfersHint')} />
      <StockFilters
        storeId={storeId}
        onStore={(id) => {
          setStoreId(id);
          paging.reset();
        }}
      >
        <Checkbox
          id="stock-differences"
          labelText={r('differencesOnly')}
          checked={differences}
          onChange={(_e, { checked }) => {
            setDifferences(checked);
            paging.reset();
          }}
        />
      </StockFilters>
      <ListTable
        columns={columns}
        rows={list.data?.items ?? []}
        ariaLabel={r('transfers')}
        loading={list.isLoading}
        error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
        onRetry={() => void list.refetch()}
        onRowClick={(x: Transfer) => setSelected(x)}
        rowLabel={(x: Transfer) => x.transferNumber}
        pagination={paging.pagination(list.data?.total)}
        empty={{ heading: r('noTransfers') }}
      />
      {selected && (
        <ListTable
          title={r('linesOf', { number: selected.transferNumber })}
          description={`${selected.fromStoreName} → ${selected.toStoreName}`}
          columns={lineColumns}
          rows={selected.lines}
          ariaLabel={r('linesOf', { number: selected.transferNumber })}
        />
      )}
    </section>
  );
}

/** Posted counts across stores with their variances: lines that differ and what the differences are worth at standard cost. */
export function HoStockCountsPage() {
  const { t } = useTranslation();
  const r = useStockText();
  const format = useFormat();
  const [storeId, setStoreId] = useState('');
  const [variances, setVariances] = useState(false);
  const [selected, setSelected] = useState<Count>();
  const paging = usePaging();
  const list = useQuery({
    queryKey: ['stock', 'ho', 'counts', storeId, variances, paging.page, paging.pageSize],
    queryFn: () =>
      unwrap(
        hoApi.GET('/api/v1/stock/ho/counts', {
          params: {
            query: {
              storeId: storeId || undefined,
              variances: variances || undefined,
              page: paging.page,
              pageSize: paging.pageSize,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const columns: ListColumn<Count>[] = [
    { key: 'countNumber', header: r('number') },
    { key: 'storeName', header: r('store') },
    { key: 'countType', header: r('type'), render: (c) => r(`countType.${c.countType}`) },
    { key: 'postedAt', header: r('postedAt'), render: (c) => format.dateTime(c.postedAt) },
    { key: 'lineCount', header: r('lineCount'), numeric: true, render: (c) => format.number(n(c.lineCount)) },
    {
      key: 'varianceLines',
      header: r('varianceLines'),
      numeric: true,
      render: (c) => format.number(n(c.varianceLines)),
    },
    {
      key: 'varianceValue',
      header: r('varianceValue'),
      numeric: true,
      render: (c) => {
        if (c.varianceValue == null) return '';
        const v = n(c.varianceValue);
        return <Tag type={v === 0 ? 'green' : v < 0 ? 'red' : 'warm-gray'}>{signed(format.number, v)}</Tag>;
      },
    },
  ];
  const lineColumns: ListColumn<CountLine>[] = [
    { key: 'item', header: r('item'), render: (l) => `${l.itemCode} – ${l.itemName}` },
    {
      key: 'expected',
      header: r('expected'),
      numeric: true,
      render: (l) => `${format.number(l.expectedQuantity)} ${l.uomCode}`,
    },
    {
      key: 'counted',
      header: r('counted'),
      numeric: true,
      render: (l) => (l.countedQuantity == null ? '' : format.number(l.countedQuantity)),
    },
    {
      key: 'difference',
      header: r('difference'),
      numeric: true,
      render: (l) => (l.difference == null ? '' : signed(format.number, n(l.difference))),
    },
  ];

  return (
    <section className="pos-admin-page pos-report">
      <PageHeader title={r('counts')} description={r('countsHint')} />
      <StockFilters
        storeId={storeId}
        onStore={(id) => {
          setStoreId(id);
          paging.reset();
        }}
      >
        <Checkbox
          id="stock-variances"
          labelText={r('variancesOnly')}
          checked={variances}
          onChange={(_e, { checked }) => {
            setVariances(checked);
            paging.reset();
          }}
        />
      </StockFilters>
      <ListTable
        columns={columns}
        rows={list.data?.items ?? []}
        ariaLabel={r('counts')}
        loading={list.isLoading}
        error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
        onRetry={() => void list.refetch()}
        onRowClick={(c: Count) => setSelected(c)}
        rowLabel={(c: Count) => c.countNumber}
        pagination={paging.pagination(list.data?.total)}
        empty={{ heading: r('noCounts') }}
      />
      {selected && (
        <ListTable
          title={r('linesOf', { number: selected.countNumber })}
          description={selected.storeName}
          columns={lineColumns}
          rows={selected.lines}
          ariaLabel={r('linesOf', { number: selected.countNumber })}
        />
      )}
    </section>
  );
}
